import {
  Component,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Inject
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { DualMonthDayPickerComponent } from '../dual-month-day-picker/dual-month-day-picker.component';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DayPickerComponent } from '../day-picker/day-picker.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';
import { OverlayComponent } from '../../overlay/overlay.component';
import { IdService } from '../../../services/id.service';
import { OverlayService } from '../../overlay/overlay.service';
import { PlatformService } from '../../../services/platform.service';
import { DatePickerService } from './date-picker.service';
import { DatePickerConfig } from './date-picker.config';
import { DatePickerInterface } from './date-picker-interface';

import type {
  OnInit,
  OnDestroy,
  TemplateRef
} from '@angular/core';
import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { DatePickerMode } from './date-picker-mode.enum';
import type { DateValidator } from './date-validator.type';
import type { DatePickerInputDirective } from './date-picker-input.directive';

/**
 * The Date Picker Component
 *
 * This is the input for selecting dates
 */
@Component({
  selector: 'td-wb-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayComponent,
    DatePickerInnerComponent,
    DayPickerComponent,
    MonthPickerComponent,
    YearPickerComponent,
    DualMonthDayPickerComponent
  ]
})
export class DatePickerComponent extends DatePickerInterface implements OnInit, OnDestroy {
  private static readonly ID_PREFIX: string = 'td-wb-date-picker';

  /**
   * Sets datepicker mode, supports: day, month, year; defaults to day
   */
  @Input() mode: DatePickerMode;

  /**
   * Sets minimum datepicker mode, supports: day, month, year; defaults to day
   */
  @Input() minMode: DatePickerMode;

  /**
   * Sets maximum datepicker mode, supports: day, month, year; defaults to year
   */
  @Input() maxMode: DatePickerMode;

  /**
   * if false week numbers will be hidden; defaults to false
   */
  @Input() showWeeks: boolean;

  /**
   * Label of the input element
   */
  @Input() label: string = '';

  /**
   * Id of the input element
   */
  @Input() datePickerId: string;

  /**
   * If true only dates of current month will be displayed.
   */
  @Input() displayDatesOfCurrentMontyOnly: boolean;

  @ViewChild('picker') content: TemplateRef<unknown>;

  /**
   * Format of day in month
   */
  formatDay: DatePickerFormatType;

  /**
   * Format of month in year.
   */
  formatMonth: DatePickerFormatType;

  /**
   * Format of year in year range.
   */
  formatYear: DatePickerFormatType;

  /**
   * Format of day in week header.
   */
  formatDayHeader: DatePickerFormatType;

  /**
   * Format of title when selecting day.
   */
  formatDayTitle: DatePickerFormatType;

  /**
   * Format of title when selecting month.
   */
  formatMonthTitle: DatePickerFormatType;

  /**
   * Starting day of the week from 0-6 (0=Sunday, ..., 6=Saturday)
   */
  startingDay: number;

  /**
   * Number of years to be displayed in the year selection
   */
  yearRange: number;

  value: Date;

  platformIOS: boolean = false;

  readonly selectedChanged: Subject<Date> = new Subject<Date>();

  private datepickerInput: DatePickerInputDirective;

  private inputSubscription: Subscription = Subscription.EMPTY;

  /**
   * The constructor of the component.
   * Sets configuration options
   *
   * @param config The date picker default configuration options
   * @param datePickerService The date picker service
   * @param overlayService to close the overlay
   * @param changeDetectorRef to reflect the change
   * @param idService
   * @param platformService
   */
  constructor(
    @Inject(DatePickerConfig) private config: DatePickerConfig,
    @Inject(DatePickerService) private datePickerService: DatePickerService,
    @Inject(OverlayService) private overlayService: OverlayService,
    @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef,
    @Inject(IdService) private idService: IdService,
    @Inject(PlatformService) private platformService: PlatformService) {
    super();
    this.setConfigureOptions();
  }

  /**
   * This function sets validators and unique id for the datepicker
   */
  ngOnInit(): void {
    this.uniqueId = this.idService.generateNamespaceSequentialId(DatePickerComponent.ID_PREFIX);
    this.platformIOS = this.platformService.isIOS();
  }

  ngOnDestroy(): void {
    this.inputSubscription.unsubscribe();
    this.selectedChanged.complete();
  }

  get format(): DatePickerFormatType {
    return this.datepickerInput === undefined ? null : this.datepickerInput.format;
  }

  get minDate(): Date | null {
    return this.datepickerInput === undefined ? null : this.datepickerInput.minDate;
  }

  get maxDate(): Date | null {
    return this.datepickerInput === undefined ? null : this.datepickerInput.maxDate;
  }

  get disabled(): boolean {
    return this.datepickerInput === undefined ? false : this.datepickerInput.disabled;
  }

  get datePickerInputId(): string {
    return this.datepickerInput !== undefined && this.datepickerInput.id !== undefined ? this.datepickerInput.id : '';
  }

  /**
   * This getter returns parsed model value.
   *
   * @returns Returns parsed model value or null
   */
  getParsed(): Date | null {
    return this.datePickerService.isDate(this.value) ? this.value : null;
  }

  /**
   * This function is called when date is selected from date picker component
   *
   * @param date Date selected from date picker component
   */
  onDatePickerChange(date: Date): void {
    this.selectedChanged.next(date);
    this.overlayService.close();
  }

  /**
   * Returns isDisabledDate function
   *
   * @returns bound isDisabledDate function
   */
  disabledDateInput(): DateValidator {
    return (date: Date): boolean => this.datepickerInput.isDisabledDate(date);
  }

  registerInput(input: DatePickerInputDirective): void {
    this.datepickerInput = input;
    this.inputSubscription =
      this.datepickerInput.valueChange
        .subscribe((value: Date | null): void => {
          this.value = value;
          this.changeDetectorRef.markForCheck();
        });
  }

  /**
   * Sets default configuration options
   */
  private setConfigureOptions(): void {
    Object.assign(this, this.config);
  }
}
